import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

import {
    buildOptimisticObservationData,
    updateObservationListCache,
    updateSummaryCacheOnCreate,
    updateSummaryCacheOnDelete,
    updateSummaryCacheOnUpdate,
} from '../utils/observation-cache';

export function useCreateObservation({ electricalProjectVersionId }: { electricalProjectVersionId: string }) {
    const { data: classificationData } = useQuery(
        elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId,
        }),
    );

    const isOffline = useOffline();

    const mutation = api.useMutation('post', '/api/v2/electrical/projects/observations/create', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body);

            const observationListQuery = elektraAppQueryOptions.project.observationsListOptions({
                electricalProjectVersionId,
            });

            const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                electricalProjectVersionId,
            });

            const observationFileListQuery = elektraAppQueryOptions.project.inspection.observations.observationFileListQueryOptions({
                electricalProjectObservationId: body!.ElectricalProjectObservationId!,
                electricalProjectVersionId,
            });

            await queryClient.cancelQueries(observationListQuery);
            await queryClient.cancelQueries(observationFileListQuery);
            await queryClient.cancelQueries(summaryOptions);

            const previousListData = queryClient.getQueryData<components['schemas']['ListElectricalProjectObservationQueryResult']>(
                observationListQuery.queryKey,
            );

            const previousFileListData = queryClient.getQueryData<components['schemas']['ListElectricalProjectObservationFileQueryResult']>(
                observationFileListQuery.queryKey,
            );

            const optimisticData = buildOptimisticObservationData({
                body,
                classificationData,
            });

            logger.debug(optimisticData);
            if (optimisticData) {
                logger.info({
                    message: 'Updating cache optimistically with',
                    data: optimisticData,
                });
                updateObservationListCache({
                    electricalProjectVersionId,
                    action: 'create',
                    observationData: optimisticData,
                    observationId: body!.ElectricalProjectObservationId!,
                });

                updateSummaryCacheOnCreate({
                    electricalProjectVersionId,
                    codeKind: optimisticData.CodeKind,
                });
            }

            else {
                toast.error('Gefaald offline op te slaan');
                logger.error('Failed to build optimistic observation data', {
                    projectVersionId: electricalProjectVersionId,
                    apiUrl: '/api/v2/electrical/projects/observations/create',
                });
            }

            return {
                previousListData,
                previousFileListData,
            };
        },
        onSettled: async (response) => {
            if (response) {
                const { ElectricalProjectObservationId } = response;
                const observationFileListQuery = elektraAppQueryOptions.project.inspection.observations.observationFileListQueryOptions({
                    electricalProjectObservationId: ElectricalProjectObservationId,
                    electricalProjectVersionId,
                });

                await queryClient.invalidateQueries(observationFileListQuery);
            }

            const observationListQuery = elektraAppQueryOptions.project.observationsListOptions({
                electricalProjectVersionId,
            });

            const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                electricalProjectVersionId,
            });

            await queryClient.invalidateQueries(observationListQuery);
            await queryClient.invalidateQueries(summaryOptions);
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    const disabled = useMemo(() => mutation.isPending && !isOffline, [mutation.isPending, isOffline]);

    return { mutation, disabled };
}

export function useUpdateObservation({
    electricalProjectVersionId,
    observationId,
}: {
    electricalProjectVersionId: string;
    observationId: string;
}) {
    const { data: classificationData } = useQuery(
        elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId,
        }),
    );

    const isOffline = useOffline();

    const mutation = api.useMutation('put', '/api/v2/electrical/projects/observations/edit', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body);

            const observationListQuery = elektraAppQueryOptions.project.observationsListOptions({
                electricalProjectVersionId,
            });

            const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                electricalProjectVersionId,
            });

            const observationFileListQuery = elektraAppQueryOptions.project.inspection.observations.observationFileListQueryOptions({
                electricalProjectObservationId: observationId,
                electricalProjectVersionId,
            });

            await queryClient.cancelQueries(observationListQuery);
            await queryClient.cancelQueries(observationFileListQuery);
            await queryClient.cancelQueries(summaryOptions);

            const previousListData = queryClient.getQueryData<components['schemas']['ListElectricalProjectObservationQueryResult']>(
                observationListQuery.queryKey,
            );

            const previousFileListData = queryClient.getQueryData<components['schemas']['ListElectricalProjectObservationFileQueryResult']>(
                observationFileListQuery.queryKey,
            );

            const currentObservation = previousListData?.Observations?.find(
                obs => obs.ElectricalProjectObservationId === observationId,
            );

            const previousCodeKind = currentObservation?.CodeKind || null;

            const optimisticData = buildOptimisticObservationData({
                body: body as components['schemas']['CreateElectricalProjectObservationCommand'],
                classificationData,
            });

            logger.debug(optimisticData);
            if (optimisticData) {
                updateObservationListCache({
                    electricalProjectVersionId,
                    action: 'update',
                    observationData: optimisticData,
                    observationId,
                });

                updateSummaryCacheOnUpdate({
                    electricalProjectVersionId,
                    newCodeKind: optimisticData.CodeKind,
                    previousCodeKind,
                });
            }
            else {
                toast.error('Gefaald offline op te slaan');
                logger.error('Failed to build optimistic observation data', {
                    projectVersionId: electricalProjectVersionId,
                    apiUrl: '/api/v2/electrical/projects/observations/edit',
                });
            }

            return {
                previousListData,
                previousFileListData,
            };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.observationsListOptions({
                    electricalProjectVersionId,
                }),
            );
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.recapViewOptions({
                    electricalProjectVersionId,
                }),
            );
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.inspection.observations.observationFileListQueryOptions({
                    electricalProjectObservationId: observationId,
                    electricalProjectVersionId,
                }),
            );
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    const disabled = useMemo(() => mutation.isPending && !isOffline, [mutation.isPending, isOffline]);

    return { mutation, disabled };
}

export function useDeleteObservation({
    electricalProjectVersionId,
    observationData,
}: {
    electricalProjectVersionId: string;
    observationData: components['schemas']['ListElectricalProjectObservationQueryResultObservation'] | null;
}) {
    const { data: classificationData } = useQuery(
        elektraAppQueryOptions.project.inspection.observations.classificationGetOptions({
            electricalProjectVersionId,
        }),
    );

    const mutation = api.useMutation('delete', '/api/v2/electrical/projects/observations/delete', {
        onMutate: async (variables) => {
            const body = variables!.params.query;

            const observationListQuery = elektraAppQueryOptions.project.observationsListOptions({
                electricalProjectVersionId,
            });

            const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                electricalProjectVersionId,
            });

            await queryClient.cancelQueries(observationListQuery);
            await queryClient.cancelQueries(summaryOptions);

            const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectObservationQueryResult']>(
                observationListQuery.queryKey,
            );

            const conversationSeverity = classificationData?.Groups?.find(
                group => group.ElectricalObservationClassificationGroupId === observationData?.Group?.ElectricalObservationClassificationGroupId,
            )?.Codes?.find(
                x => x.ElectricalObservationClassificationCodeId === observationData?.Code?.ElectricalObservationClassificationCodeId,
            )?.Kind;

            if (observationData) {
                updateSummaryCacheOnDelete({
                    electricalProjectVersionId,
                    codeKind: conversationSeverity,
                });
            }

            if (previousData) {
                queryClient.setQueryData<components['schemas']['ListElectricalProjectObservationQueryResult']>(
                    observationListQuery.queryKey,
                    {
                        ...previousData,
                        Observations: previousData.Observations?.filter(
                            observation => observation.ElectricalProjectObservationId !== body.ElectricalProjectObservationId,
                        ),
                        Count: (previousData.Count || 1) - 1,
                    },
                );
            }
        },
        onSettled: async () => {
            const summaryOptions = elektraAppQueryOptions.project.recapViewOptions({
                electricalProjectVersionId,
            });
            await queryClient.invalidateQueries(
                elektraAppQueryOptions.project.observationsListOptions({
                    electricalProjectVersionId,
                }),
            );
            await queryClient.invalidateQueries(summaryOptions);
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    return { mutation };
}
